/**
 * One Euro filter (Casiez, Roussel & Vogel, CHI 2012).
 *
 * Chosen over plain exponential smoothing because it is velocity-aware: it filters hard when
 * the hand is nearly still (killing landmark jitter that would otherwise warble the pitch)
 * and relaxes as the hand speeds up (so fast gestures stay responsive). That trade-off is
 * exactly what a theremin needs.
 *
 * Defaults favour immediacy over visual perfection, as required by the product brief.
 */

export interface OneEuroConfig {
  /** Lower = smoother but laggier when the input is slow. Hz. */
  minCutoff: number;
  /** Higher = more responsive when the input moves fast. */
  beta: number;
  /** Cutoff for the derivative estimate. Hz. */
  dCutoff: number;
}

export const DEFAULT_ONE_EURO: OneEuroConfig = {
  minCutoff: 1.7,
  beta: 0.035,
  dCutoff: 1.0,
};

/** Smoothing slider (0 = rawest, 1 = smoothest) -> One Euro parameters. */
export function configFromSmoothingAmount(amount: number): OneEuroConfig {
  const t = amount < 0 ? 0 : amount > 1 ? 1 : amount;
  return {
    // 6 Hz cutoff at t=0 (essentially raw) down to 0.5 Hz at t=1 (very smooth).
    minCutoff: 6 - 5.5 * t,
    // Keep some velocity adaptation at every setting so fast moves never feel stuck.
    beta: 0.12 - 0.1 * t,
    dCutoff: 1.0,
  };
}

/**
 * A softer variant of a configuration, for values that are only ever *looked at*.
 *
 * The skeleton overlay and the openness reading can afford more lag than the control point
 * can: nobody hears a fingertip arriving two frames late, but everybody sees it shimmering.
 * Lowering the cutoff (and the velocity adaptation with it) buys visible steadiness for
 * latency that does not reach the audio path.
 */
export function softenConfig(config: OneEuroConfig, factor = 0.55): OneEuroConfig {
  const scale = factor <= 0 ? 1 : factor;
  return {
    minCutoff: Math.max(0.2, config.minCutoff * scale),
    beta: config.beta * scale,
    dCutoff: config.dCutoff,
  };
}

function alphaFor(cutoffHz: number, dtSeconds: number): number {
  const tau = 1 / (2 * Math.PI * cutoffHz);
  return 1 / (1 + tau / dtSeconds);
}

/** Scalar One Euro filter. Allocation-free after construction. */
export class OneEuroFilter {
  private config: OneEuroConfig;

  private value = 0;

  private derivative = 0;

  private lastTime = 0;

  private initialized = false;

  constructor(config: Partial<OneEuroConfig> = {}) {
    this.config = { ...DEFAULT_ONE_EURO, ...config };
  }

  setConfig(config: Partial<OneEuroConfig>): void {
    this.config = { ...this.config, ...config };
  }

  reset(): void {
    this.initialized = false;
    this.value = 0;
    this.derivative = 0;
    this.lastTime = 0;
  }

  get current(): number {
    return this.value;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * @param raw        new observation
   * @param timeSeconds monotonic timestamp in seconds
   */
  filter(raw: number, timeSeconds: number): number {
    if (!Number.isFinite(raw)) return this.value;

    if (!this.initialized) {
      this.initialized = true;
      this.value = raw;
      this.derivative = 0;
      this.lastTime = timeSeconds;
      return raw;
    }

    // Guard against zero/negative/absurd dt from throttled or restored timers.
    let dt = timeSeconds - this.lastTime;
    if (!(dt > 0) || dt > 1) dt = 1 / 60;
    this.lastTime = timeSeconds;

    const rawDerivative = (raw - this.value) / dt;
    const dAlpha = alphaFor(this.config.dCutoff, dt);
    this.derivative += dAlpha * (rawDerivative - this.derivative);

    const cutoff = this.config.minCutoff + this.config.beta * Math.abs(this.derivative);
    const alpha = alphaFor(cutoff, dt);
    this.value += alpha * (raw - this.value);
    return this.value;
  }

  /** Jump straight to a value without ramping (used when a track is re-acquired). */
  snapTo(value: number, timeSeconds: number): void {
    this.initialized = true;
    this.value = value;
    this.derivative = 0;
    this.lastTime = timeSeconds;
  }

  /** Estimated rate of change in units per second. */
  get velocity(): number {
    return this.derivative;
  }
}

/**
 * Two One Euro filters sharing one configuration, for a 2D landmark.
 *
 * All 21 landmarks of both hands go through one of these every frame, which is why it is 2D:
 * MediaPipe's z is not metric and nothing downstream reads it per landmark, so filtering it
 * would be 42 filters of pure cost.
 */
export class Point2Smoother {
  private readonly fx = new OneEuroFilter();

  private readonly fy = new OneEuroFilter();

  readonly value = { x: 0, y: 0 };

  readonly velocity = { x: 0, y: 0 };

  constructor(config: Partial<OneEuroConfig> = {}) {
    this.setConfig(config);
  }

  setConfig(config: Partial<OneEuroConfig>): void {
    this.fx.setConfig(config);
    this.fy.setConfig(config);
  }

  get isInitialized(): boolean {
    return this.fx.isInitialized;
  }

  update(x: number, y: number, timeSeconds: number): void {
    this.value.x = this.fx.filter(x, timeSeconds);
    this.value.y = this.fy.filter(y, timeSeconds);
    this.velocity.x = this.fx.velocity;
    this.velocity.y = this.fy.velocity;
  }

  snapTo(x: number, y: number, timeSeconds: number): void {
    this.fx.snapTo(x, timeSeconds);
    this.fy.snapTo(y, timeSeconds);
    this.value.x = x;
    this.value.y = y;
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  reset(): void {
    this.fx.reset();
    this.fy.reset();
    this.value.x = this.value.y = 0;
    this.velocity.x = this.velocity.y = 0;
  }
}

export interface SmoothedPoint {
  x: number;
  y: number;
  z: number;
}

/** Three One Euro filters sharing one configuration, for a 3D control point. */
export class Vector3Smoother {
  private readonly fx = new OneEuroFilter();

  private readonly fy = new OneEuroFilter();

  private readonly fz = new OneEuroFilter();

  readonly value: SmoothedPoint = { x: 0, y: 0, z: 0 };

  readonly velocity: SmoothedPoint = { x: 0, y: 0, z: 0 };

  constructor(config: Partial<OneEuroConfig> = {}) {
    this.setConfig(config);
  }

  setConfig(config: Partial<OneEuroConfig>): void {
    this.fx.setConfig(config);
    this.fy.setConfig(config);
    this.fz.setConfig(config);
  }

  reset(): void {
    this.fx.reset();
    this.fy.reset();
    this.fz.reset();
    this.value.x = this.value.y = this.value.z = 0;
    this.velocity.x = this.velocity.y = this.velocity.z = 0;
  }

  get isInitialized(): boolean {
    return this.fx.isInitialized;
  }

  update(x: number, y: number, z: number, timeSeconds: number): SmoothedPoint {
    this.value.x = this.fx.filter(x, timeSeconds);
    this.value.y = this.fy.filter(y, timeSeconds);
    this.value.z = this.fz.filter(z, timeSeconds);
    this.velocity.x = this.fx.velocity;
    this.velocity.y = this.fy.velocity;
    this.velocity.z = this.fz.velocity;
    return this.value;
  }

  snapTo(x: number, y: number, z: number, timeSeconds: number): void {
    this.fx.snapTo(x, timeSeconds);
    this.fy.snapTo(y, timeSeconds);
    this.fz.snapTo(z, timeSeconds);
    this.value.x = x;
    this.value.y = y;
    this.value.z = z;
    this.velocity.x = this.velocity.y = this.velocity.z = 0;
  }
}
